import { useAppDispatch, useAppSelector } from "@app/hooks"
import OptionsButton2 from "@cmp/buttons/optionsButton2"
import GenericTable from "@cmp/genericTable"
import { ErrorComponent } from "@cmp/loaders/errorComponent"
import { LoadingComponent } from "@cmp/loaders/loadingComponent"
import {
  editFeatureFlagsPopupOpened,
  editRolesRequested,
  editUserRequested,
  fetchTutorsProperties,
  fetchUsersWithRoles,
  offboardStaffRequested,
  onboardStaffRequested,
  setSearch,
} from "@features/account-management/accountManagementSlice"
import { ALL_ROLES, Role } from "@features/login/loginSlice"
import {
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  SvgIcon,
  Tooltip,
  Typography,
} from "@mui/material"
import { ProgramStaffClient } from "@clients/programStaffClient"
import { CalendarAssignmentsPopover } from "./calendarAssignmentsPopover"
import appIcons from "@utils/appIcons"
import { useEffect, useState } from "react"
import appTheme from "../theme/appTheme"
import EditRolesDialog from "./editRolesDialog"
import OffboardStaffDialog from "./offboardStaffDialog"
import OnboardStaffDialog from "./onboard-staff-dialog/onboardStaffDialog"
import roleDisplayNames from "./roleDisplayName"
import EditEmployeeDetailsDialog from "./editEmployeeDetailsDialog"
import EditUserFeatureFlagsDialog from "./editUserFeatureFlagsDialog"
import { UsersClient } from "@clients/usersClient"
import { store } from "@app/store"
import { showErrorSnackbar, showSuccessSnackbar } from "@features/ui/uiSlice"
import { SearchComponent } from "@cmp/searchComponent"
import { FilterName, SearchAdvanceFilter } from "@features/search/searchSlice"

function AccountManagement() {
  const accounts = useAppSelector((state) => state.accountManagement.accounts)
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (accounts.status === "idle") {
      dispatch(fetchUsersWithRoles())
      dispatch(fetchTutorsProperties())
    }
  }, [accounts.status, dispatch])

  // Who is one warning away from a "my calendar is empty" ticket: instructors
  // staffed somewhere with zero assigned classes (school-wide, one request).
  const [zeroClassInstructors, setZeroClassInstructors] =
    useState<Set<string> | null>(null)
  useEffect(() => {
    ProgramStaffClient.listInstructorsWithoutClasses()
      .then((ids) => setZeroClassInstructors(new Set(ids)))
      .catch(() => setZeroClassInstructors(new Set()))
  }, [])

  const [calendarPopover, setCalendarPopover] = useState<{
    userId: string
    name: string
    email: string
    anchor: HTMLElement
  } | null>(null)

  const isLoading = accounts.status === "pending" || accounts.status === "idle"
  const isError = accounts.status === "error"

  const search = useAppSelector((state) => state.accountManagement.search)
  const [selectedRoles, setSelectedRoles] = useState<Role[]>([
    Role.MasterschoolEmployee,
  ])
  const inputFilters: SearchAdvanceFilter[] = [
    {
      filterName: FilterName.Role,
      selectedValues: selectedRoles,
      optionalValues: ALL_ROLES,
    },
  ]
  const onFiltersChange = (filters: SearchAdvanceFilter[]) => {
    setSelectedRoles(filters[0].selectedValues as Role[])
  }

  const users = accounts.status !== "success" ? [] : accounts.data
  const filteredUsers = users
    .filter(
      (user) =>
        `${user.firstName.toLowerCase()} ${user.lastName.toLowerCase()}`.includes(
          search.toLowerCase(),
        ) ||
        user.email.toLowerCase().includes(search.toLowerCase()) ||
        user.roles.some((role) =>
          role.toLowerCase().includes(search.toLowerCase()),
        ),
    )
    .filter(
      (user) =>
        selectedRoles.length === 0 ||
        user.roles.some((role) => selectedRoles.includes(role as Role)),
    )
  const displayedUsersNumberText =
    search.length > 0
      ? `(${filteredUsers.length}/${users.length})`
      : `(${users.length})`

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
        paddingX: "24px",
        paddingBottom: "48px",
      }}
    >
      <OnboardStaffDialog />
      <OffboardStaffDialog />
      <EditRolesDialog />
      <EditEmployeeDetailsDialog />
      <EditUserFeatureFlagsDialog />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          maxWidth: "1240px",
          marginX: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            pt: "80px",
            mb: "16px",
            gap: "16px",
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h4">
              Employees &amp; Faculty{" "}
              <span
                style={{
                  color: appTheme.palette.text.disabled,
                }}
              >
                {displayedUsersNumberText}
              </span>
            </Typography>
            {isLoading && (
              <LoadingComponent loadedComponents="employees & staff" />
            )}
            {isError && <ErrorComponent loadedComponents="employees & staff" />}
          </Box>
          {/* The calendar rule, stated where accounts are managed. */}
          <Typography variant="body2" color="text.secondary">
            Instructors see their personal calendar plus one calendar per class
            assigned on each program&apos;s Staff page.
            {zeroClassInstructors !== null && zeroClassInstructors.size > 0 && (
              <Typography component="span" variant="body2" color="warning.main">
                {" "}
                {zeroClassInstructors.size} instructor
                {zeroClassInstructors.size === 1 ? " has" : "s have"} no classes
                assigned.
              </Typography>
            )}{" "}
            The view as buttons open Campus or the LMS as that person in a new
            tab — your own session stays logged in.
          </Typography>
          <Box display="flex" gap="16px" width="100%">
            <SearchComponent
              searchText={search}
              onSearchTextChange={(text) => dispatch(setSearch(text))}
              inputFilters={inputFilters}
              onInputFiltersChange={onFiltersChange}
              getOptionLabel={(role) => roleDisplayNames(role as Role)}
            />
            <Button
              variant="contained"
              color="primary"
              style={{
                width: "fit-content",
                flexShrink: 0,
              }}
              size="medium"
              onClick={() => {
                dispatch(onboardStaffRequested())
              }}
              startIcon={
                <SvgIcon
                  component={appIcons.plus}
                  sx={{
                    stroke: appTheme.palette.primary.contrastText,
                    fill: "inherit",
                    width: "20px",
                    height: "20px",
                  }}
                  inheritViewBox
                />
              }
            >
              Onboard staff
            </Button>
          </Box>
          <GenericTable
            data={filteredUsers.map((user) => ({
              ...user,
              person: user.id,
              calendar: user.id,
            }))}
            keys={["person", "roles", "calendar", "slackId", "id"]}
            sx={{
              width: "100%",
              // One scroll context (the page), never a grid-in-page scrollbox;
              // the header stays pinned against the page scroll instead.
              overflow: "visible",
              "& thead th": {
                position: "sticky",
                top: 0,
                zIndex: 2,
                backgroundColor: appTheme.palette.background.paper,
              },
            }}
            renderHeader={{
              id: () => undefined,
              person: () => (
                <Typography variant="body1_sb">Employee</Typography>
              ),
              calendar: () => (
                <Box display="flex" alignItems="center" gap="4px">
                  <Typography variant="body1_sb">Calendar</Typography>
                  <Tooltip
                    title="What this person's calendar holds: their personal calendar plus one calendar per assigned class. Click a cell for the breakdown."
                    placement="top"
                  >
                    <SvgIcon
                      component={appIcons.infoCircle}
                      inheritViewBox
                      sx={{
                        width: 14,
                        height: 14,
                        fill: "none",
                        color: "text.secondary",
                      }}
                    />
                  </Tooltip>
                </Box>
              ),
              slackId: () => <Typography variant="body1_sb">Slack</Typography>,
            }}
            render={{
              person: (personId, user) => (
                <Box display="flex" alignItems="center" gap="12px">
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      fontSize: 13,
                      bgcolor: "grey.100",
                      color: "text.primary",
                    }}
                  >
                    {user.firstName?.[0]}
                    {user.lastName?.[0]}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {user.firstName} {user.lastName}
                    </Typography>
                    <Typography variant="body3" color="text.secondary">
                      {user.email}
                    </Typography>
                  </Box>
                </Box>
              ),
              roles: renderRoles,
              calendar: (calendarId, user) => {
                if (!user.roles.includes(Role.Instructor)) {
                  return (
                    <Typography variant="body3" color="text.disabled">
                      —
                    </Typography>
                  )
                }
                const openPopover = (event: React.MouseEvent<HTMLElement>) =>
                  setCalendarPopover({
                    userId: user.id,
                    name: `${user.firstName} ${user.lastName}`,
                    email: user.email,
                    anchor: event.currentTarget,
                  })
                if (zeroClassInstructors?.has(user.id)) {
                  return (
                    <Tooltip
                      title="No classes assigned anywhere — they see only their personal calendar. Click for details."
                      placement="top"
                    >
                      <Chip
                        icon={
                          <SvgIcon
                            component={appIcons.alertTriangle}
                            inheritViewBox
                            sx={{ fill: "none", width: 14, height: 14 }}
                          />
                        }
                        label="Personal only"
                        size="small"
                        color="warning"
                        variant="outlined"
                        onClick={openPopover}
                      />
                    </Tooltip>
                  )
                }
                return (
                  <Tooltip
                    title="Calendars & class assignments"
                    placement="top"
                  >
                    <IconButton size="small" onClick={openPopover}>
                      <SvgIcon
                        component={appIcons.calendar}
                        inheritViewBox
                        sx={{ width: 18, height: 18, fill: "none" }}
                      />
                    </IconButton>
                  </Tooltip>
                )
              },
              id: (id, user) => (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-end"
                  gap="4px"
                >
                  <Tooltip
                    title="Open Campus as this user — new tab, your session stays"
                    placement="top"
                  >
                    <Button
                      size="small"
                      sx={{ flexShrink: 0 }}
                      startIcon={
                        <SvgIcon
                          component={appIcons.eye}
                          inheritViewBox
                          sx={{ width: 16, height: 16, fill: "none" }}
                        />
                      }
                      onClick={() => viewAsUser(user.email)}
                    >
                      Campus
                    </Button>
                  </Tooltip>
                  <Tooltip
                    title="Open the LMS as this user — new tab, your session stays"
                    placement="top"
                  >
                    <Button
                      size="small"
                      sx={{ flexShrink: 0 }}
                      startIcon={
                        <SvgIcon
                          component={appIcons.eye}
                          inheritViewBox
                          sx={{ width: 16, height: 16, fill: "none" }}
                        />
                      }
                      onClick={() => viewAsUser(user.email, "lms")}
                    >
                      LMS
                    </Button>
                  </Tooltip>
                  <ActionsMenu userId={id} email={user.email} />
                </Box>
              ),
              slackId: (slackId) => <SlackIdCell slackId={slackId} />,
            }}
          />
        </Box>
      </Box>
      {calendarPopover && (
        <CalendarAssignmentsPopover
          anchorEl={calendarPopover.anchor}
          onClose={() => setCalendarPopover(null)}
          userClientId={calendarPopover.userId}
          name={calendarPopover.name}
          email={calendarPopover.email}
        />
      )}
    </Box>
  )
}

export function renderRoles(roles: string[]) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        gap: "4px",
        maxWidth: "200px",
      }}
    >
      {roles.map((role: string) => (
        <RoleChip key={role} role={role as Role} />
      ))}
    </Box>
  )
}

export const SlackIdCell = (props: { slackId: string | undefined }) => {
  const { slackId } = props
  if (!slackId) {
    return null
  }
  const link =
    "https://masterschool-campus.slack.com/app_redirect?channel=" + slackId
  return (
    <Tooltip title="Send message" arrow placement="bottom">
      <IconButton href={link} target="_blank">
        <SvgIcon
          component={appIcons.slackIcon}
          inheritViewBox
          sx={{ width: "20px", height: "20px" }}
        />
      </IconButton>
    </Tooltip>
  )
}

function RoleChip(props: { role: Role }) {
  return (
    <Chip
      label={roleDisplayNames(props.role)}
      variant="outlined"
      size="small"
      sx={{
        width: "fit-content",
      }}
    />
  )
}

const ActionsMenu = (props: { userId: string; email: string }) => {
  const dispatch = useAppDispatch()

  return (
    <OptionsButton2
      buttonModel={{
        type: "icon",
        props: {
          sx: {
            marginLeft: "auto",
            display: "flex",
          },
          size: "small",
          children: (
            <SvgIcon
              component={appIcons.dotsHorizontal}
              inheritViewBox
              sx={{ color: "primary.main" }}
            />
          ),
        },
      }}
      items={[
        {
          type: "menu-item",
          props: {
            key: "edit-role",
            children: "Edit roles",
            onClick: () => {
              dispatch(editRolesRequested(props))
            },
          },
        },
        {
          type: "menu-item",
          props: {
            key: "offboard",
            children: "Offboard",
            onClick: () => {
              dispatch(offboardStaffRequested(props))
            },
          },
        },
        {
          type: "menu-item",
          props: {
            key: "edit-user",
            children: "Edit user details",
            onClick: () => {
              dispatch(editUserRequested(props))
            },
          },
        },
        {
          type: "menu-item",
          props: {
            key: "feature-flags",
            children: "Edit feature flags",
            onClick: () => {
              dispatch(editFeatureFlagsPopupOpened(props))
            },
          },
        },
        {
          type: "menu-item",
          props: {
            key: "view-as",
            children: "View as in Campus",
            onClick: () => viewAsUser(props.email),
          },
        },
        {
          type: "menu-item",
          props: {
            key: "view-as-lms",
            children: "View as in LMS",
            onClick: () => viewAsUser(props.email, "lms"),
          },
        },
        {
          type: "menu-item",
          props: {
            key: "impersonate",
            children: "Copy impersonation link",
            onClick: () => generateLinkForUser(props.email),
          },
        },
        {
          type: "menu-item",
          props: {
            key: "impersonate-lms",
            children: "Copy LMS impersonation link",
            onClick: () => generateLinkForUser(props.email, "lms"),
          },
        },
      ]}
    />
  )
}

export default AccountManagement

export async function generateLinkForUser(
  email: string,
  target: "campus" | "lms" = "campus",
) {
  if (!email) {
    return
  }
  await UsersClient.generateImpersonationLink(email, target)
    .then((link) => {
      navigator.clipboard.writeText(link)
      store.dispatch(
        showSuccessSnackbar(
          `${
            target === "lms" ? "LMS" : "Campus"
          } link copied. Open the URL in incognito mode.`,
        ),
      )
    })
    .catch(() => {
      store.dispatch(
        showErrorSnackbar(
          "Failed to copy impersonation link, try again later.",
        ),
      )
      return undefined
    })
}

// One-click impersonation: opens a NEW TAB whose session is tab-scoped
// (?impersonate=1 makes the login proxy keep the token in sessionStorage),
// so the admin's own login in this and every other tab is untouched.
export async function viewAsUser(
  email: string,
  target: "campus" | "lms" = "campus",
) {
  if (!email) {
    return
  }
  await UsersClient.generateImpersonationLink(email, target)
    .then((link) => {
      const url = link.includes("?")
        ? `${link}&impersonate=1`
        : `${link}?impersonate=1`
      window.open(url, "_blank", "noopener")
      store.dispatch(
        showSuccessSnackbar(
          `Opened ${
            target === "lms" ? "the LMS" : "Campus"
          } as this user in a new tab — your own session is untouched.`,
        ),
      )
    })
    .catch(() => {
      store.dispatch(
        showErrorSnackbar("Failed to open impersonation, try again later."),
      )
      return undefined
    })
}
